import React, { ReactNode, createContext, useCallback, useEffect, useMemo, useState } from 'react';

import { LayoutAreaWrapper } from './components/LayoutAreaWrappers';
import { TabbedContent } from './components/TabbedContent';
import { ContentConflictResolver } from './services/ContentConflictResolver';
import { AutoPanelContextType, LayoutArea, ModalConfig, ModalType, PanelConfig, PanelState, SidebarPosition } from './types';
import { isValidContent } from './utils/contentDetection';
import { exposeDebugHelpers, logDebugSnapshot, validateConfiguration } from './utils/debugHelpers';

// Create the context
export const AutoPanelContext = createContext<AutoPanelContextType | null>(null);

interface AutoPanelProviderProps {
  children: ReactNode;
  onPanelChange?: (area: LayoutArea, isVisible: boolean, size?: number) => void;
}

/**
 * Utility function to automatically wrap content with the appropriate layout area wrapper.
 * This eliminates the need for developers to manually wrap content.
 */
const wrapContentWithLayoutArea = (content: ReactNode, area: LayoutArea): ReactNode => {
  // Don't wrap if content is null/undefined or already a React element with layout wrapper
  if (!content) return content;
  // Check if content is already wrapped with a layout wrapper or is a complete layout component
  if (React.isValidElement(content)) {
    const elementType = content.type;
    if (typeof elementType === 'function') {
      const componentName = elementType.name || '';
      // Skip wrapping for components that are already layout components
      if (componentName.includes('Wrapper') || componentName.includes('TabbedContent') || componentName.includes('Content')) {
        console.log(`[AutoPanelProvider] Content already a layout component (${componentName}) for area=${area}, skipping auto-wrap`);
        return content;
      }
    }
  }

  console.log(`[AutoPanelProvider] Auto-wrapping content for area=${area}`);
  return <LayoutAreaWrapper area={area}>{content}</LayoutAreaWrapper>;
};

/**
 * Provider component that manages panel state and provides AutoLayout commands.
 *
 * NOTE: This is an INTERNAL component. Do not use directly!
 * Use <AutoPanelLayout> instead, which includes this provider automatically.
 */
export const AutoPanelProvider: React.FC<AutoPanelProviderProps> = ({ children, onPanelChange }) => {
  // Panel state management
  const [panelStates, setPanelStates] = useState<Map<LayoutArea, PanelState>>(new Map());

  // Modal state
  const [_modalContent, setModalContent] = useState<ReactNode>(null);
  const [_modalConfig, setModalConfig] = useState<ModalConfig | null>(null);
  const [_isModalVisible, setIsModalVisible] = useState(false);

  // Drawer state
  const [_drawerContent, setDrawerContent] = useState<ReactNode>(null);
  const [_drawerPosition, setDrawerPosition] = useState<SidebarPosition | null>(null);
  const [_isDrawerVisible, setIsDrawerVisible] = useState(false);

  // Development debug helpers
  useEffect(() => {
    exposeDebugHelpers();
  }, []);

  // Development validation and debugging
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      const validation = validateConfiguration(panelStates);
      if (validation.errors.length > 0 || validation.warnings.length > 0) {
        console.group('🔧 AutoPanelLayout Configuration Check');
        if (validation.errors.length > 0) {
          console.error('Errors found:', validation.errors);
        }
        if (validation.warnings.length > 0) {
          console.warn('Warnings found:', validation.warnings);
        }
        console.groupEnd();
      }
    }
  }, [panelStates]);

  // Calculate active panels based on content validity
  const activePanels = useMemo(() => {
    const active: LayoutArea[] = [LayoutArea.CONTENT]; // Main content always present

    panelStates.forEach((state, area) => {
      if (area !== LayoutArea.CONTENT && state.isVisible && isValidContent(state.content)) {
        active.push(area);
      }
    });

    return active;
  }, [panelStates]);

  // Panel management methods
  const setPanel = useCallback(
    (
      area: LayoutArea,
      content: ReactNode,
      config?: Partial<PanelConfig> & {
        contentId?: string;
        priority?: number;
        source?: string;
        forceReplace?: boolean;
      },
    ) => {
      const isContentValid = isValidContent(content);

      setPanelStates((prev) => {
        const newMap = new Map(prev);
        const currentState = newMap.get(area);

        // Generate content ID if not provided
        const contentId = config?.contentId ?? `${area}-${Date.now()}`;

        // Automatically wrap content with appropriate layout wrapper
        const wrappedContent = wrapContentWithLayoutArea(content, area);

        // Use conflict resolver to handle potential conflicts
        const resolution = ContentConflictResolver.resolveConflict(area, wrappedContent, contentId, currentState, {
          priority: config?.priority || 0,
          source: config?.source,
          forceReplace: config?.forceReplace,
        });

        let finalContent: ReactNode = wrappedContent;

        // Check if we should preserve existing TabbedContent or create new TabbedContent
        const hasMultipleEntries = resolution.entries.length > 1;
        const currentStateHasTabbedContent =
          currentState?.metadata?.conflictResolution?.strategy === 'tabs' &&
          currentState?.metadata?.conflictEntries &&
          currentState.metadata.conflictEntries.length > 1;

        // If resolution uses tabs strategy OR we already have tabs and still have multiple entries, use TabbedContent
        if ((resolution.strategy === 'tabs' && hasMultipleEntries) || (currentStateHasTabbedContent && hasMultipleEntries)) {
          console.log(
            `[AutoPanelProvider] Creating TabbedContent for area=${area} with ${resolution.entries.length} entries (strategy=${resolution.strategy}, preserving=${currentStateHasTabbedContent})`,
          );

          // Force tabs strategy if we have multiple entries (override replace strategy when inappropriate)
          if (resolution.strategy === 'replace' && hasMultipleEntries) {
            console.log(`[AutoPanelProvider] ⚠️  Overriding replace strategy to tabs for multiple entries in area=${area}`);
            resolution.strategy = 'tabs';
          }

          finalContent = (
            <TabbedContent
              key={`tabbed-${area}`}
              entries={resolution.entries}
              activeEntryId={currentState?.metadata?.activeEntryId || resolution.activeEntry || contentId}
              area={area}
              onTabChange={(entryId: string) => {
                console.log(`🔄 [AutoPanelProvider] Tab switch: area=${area}, newActive=${entryId}`);

                // Update metadata AND recreate TabbedContent with new active tab
                setPanelStates((prev) => {
                  const newMap = new Map(prev);
                  const currentState = newMap.get(area);
                  if (currentState?.metadata?.conflictEntries) {
                    // Find the selected entry
                    const selectedEntry = currentState.metadata.conflictEntries.find((entry) => entry.id === entryId);
                    if (selectedEntry) {
                      // Create new TabbedContent with updated active tab
                      const updatedTabbedContent = (
                        <TabbedContent
                          key={`tabbed-${area}-${entryId}`}
                          entries={currentState.metadata.conflictEntries}
                          activeEntryId={entryId}
                          area={area}
                          onTabChange={(newEntryId: string) => {
                            // Recursive call for further tab changes
                            setPanelStates((innerPrev) => {
                              const innerNewMap = new Map(innerPrev);
                              const innerCurrentState = innerNewMap.get(area);
                              if (innerCurrentState?.metadata) {
                                innerNewMap.set(area, {
                                  ...innerCurrentState,
                                  metadata: {
                                    ...innerCurrentState.metadata,
                                    activeEntryId: newEntryId,
                                  },
                                });
                              }
                              return innerNewMap;
                            });
                          }}
                        />
                      );

                      newMap.set(area, {
                        ...currentState,
                        content: updatedTabbedContent,
                        metadata: {
                          ...currentState.metadata,
                          activeEntryId: entryId,
                        },
                      });

                      console.log(`✅ [AutoPanelProvider] Tab switch completed: area=${area}, activeTab=${entryId}`);
                    }
                  }
                  return newMap;
                });
              }}
            />
          );
        }

        const newState: PanelState = {
          content: finalContent,
          isVisible: isContentValid,
          size: typeof config?.defaultSize === 'number' ? config.defaultSize : config?.defaultSize?.value || currentState?.size || 25,
          isCollapsed: false,
          metadata: {
            contentId,
            activeEntryId: resolution.activeEntry || contentId,
            conflictEntries: resolution.entries,
            conflictResolution: resolution,
            source: config?.source,
            priority: config?.priority || 0,
          },
        };

        console.log(`🔵 [AutoPanelProvider] FINAL STATE SET for area=${area}:`, {
          contentType: (finalContent as { type?: { name?: string } })?.type?.name ?? typeof finalContent,
          hasContent: !!finalContent,
          isVisible: isContentValid,
          contentId,
          entriesCount: resolution?.entries?.length ?? 0,
        });

        newMap.set(area, newState);

        // Debug snapshot in development
        if (process.env.NODE_ENV === 'development' && newMap.size > 0) {
          // Only log debug snapshot for complex state changes (conflicts, tabs, etc)
          const hasConflicts = resolution && resolution.entries.length > 1;
          if (hasConflicts) {
            setTimeout(() => logDebugSnapshot(newMap, `Panel ${area} Updated`), 0);
          }
        }
        return newMap;
      });

      // Notify parent of panel change
      if (onPanelChange) {
        const defaultSize = config?.defaultSize;
        const sizeValue = typeof defaultSize === 'number' ? defaultSize : defaultSize?.value || 25;
        onPanelChange(area, isContentValid, sizeValue);
      }
    },
    [onPanelChange],
  );

  const clearPanel = useCallback(
    (area: LayoutArea) => {
      setPanelStates((prev) => {
        const newMap = new Map(prev);
        const currentState = newMap.get(area);

        if (currentState) {
          newMap.set(area, {
            ...currentState,
            content: null,
            isVisible: false,
          });
        }

        return newMap;
      });

      // Notify parent of panel change
      if (onPanelChange) {
        onPanelChange(area, false);
      }
    },
    [onPanelChange],
  );

  const togglePanel = useCallback(
    (area: LayoutArea) => {
      setPanelStates((prev) => {
        const newMap = new Map(prev);
        const currentState = newMap.get(area);

        if (currentState) {
          const newVisibility = !currentState.isVisible;
          newMap.set(area, {
            ...currentState,
            isVisible: newVisibility && isValidContent(currentState.content),
          });

          // Notify parent of panel change
          if (onPanelChange) {
            onPanelChange(area, newVisibility);
          }
        }

        return newMap;
      });
    },
    [onPanelChange],
  );

  const collapsePanel = useCallback((area: LayoutArea) => {
    setPanelStates((prev) => {
      const newMap = new Map(prev);
      const currentState = newMap.get(area);

      if (currentState) {
        newMap.set(area, {
          ...currentState,
          isCollapsed: true,
        });
      }

      return newMap;
    });
  }, []);

  const expandPanel = useCallback((area: LayoutArea) => {
    setPanelStates((prev) => {
      const newMap = new Map(prev);
      const currentState = newMap.get(area);

      if (currentState) {
        newMap.set(area, {
          ...currentState,
          isCollapsed: false,
        });
      }

      return newMap;
    });
  }, []);

  const resizePanel = useCallback((area: LayoutArea, size: number) => {
    setPanelStates((prev) => {
      const newMap = new Map(prev);
      const currentState = newMap.get(area);

      if (currentState) {
        newMap.set(area, {
          ...currentState,
          size,
        });
      }

      return newMap;
    });
  }, []);

  // Panel state query methods
  const isPanelVisible = useCallback(
    (area: LayoutArea): boolean => {
      const state = panelStates.get(area);
      return state?.isVisible ?? false;
    },
    [panelStates],
  );

  const isPanelCollapsed = useCallback(
    (area: LayoutArea): boolean => {
      const state = panelStates.get(area);
      return state?.isCollapsed ?? false;
    },
    [panelStates],
  );

  const getPanelSize = useCallback(
    (area: LayoutArea): number | undefined => {
      const state = panelStates.get(area);
      return state?.size;
    },
    [panelStates],
  );

  // Modal management
  const showModal = useCallback((content: ReactNode, config?: ModalConfig) => {
    setModalContent(content);
    setModalConfig(config || { type: ModalType.DIALOG });
    setIsModalVisible(true);
  }, []);

  const hideModal = useCallback(() => {
    setIsModalVisible(false);
    setModalContent(null);
    setModalConfig(null);
  }, []);

  // Drawer management
  const showDrawer = useCallback((position: SidebarPosition, content: ReactNode) => {
    setDrawerPosition(position);
    setDrawerContent(content);
    setIsDrawerVisible(true);
  }, []);

  const hideDrawer = useCallback(() => {
    setIsDrawerVisible(false);
    setDrawerContent(null);
    setDrawerPosition(null);
  }, []);

  // Create context value
  const contextValue = useMemo<AutoPanelContextType>(
    () => ({
      // Panel management
      setPanel,
      clearPanel,
      togglePanel,
      collapsePanel,
      expandPanel,
      resizePanel,

      // Panel state queries
      isPanelVisible,
      isPanelCollapsed,
      getPanelSize,

      // Modal and drawer management
      showModal,
      hideModal,
      showDrawer,
      hideDrawer,

      // Layout state
      activePanels,
      panelStates,
    }),
    [
      setPanel,
      clearPanel,
      togglePanel,
      collapsePanel,
      expandPanel,
      resizePanel,
      isPanelVisible,
      isPanelCollapsed,
      getPanelSize,
      showModal,
      hideModal,
      showDrawer,
      hideDrawer,
      activePanels,
      panelStates,
    ],
  );

  return (
    <AutoPanelContext.Provider value={contextValue}>
      {children}
      {/* TODO: Add Modal and Drawer components here when needed */}
    </AutoPanelContext.Provider>
  );
};
